import React, { useState } from 'react';
import { TylerState, updateReact, useTylerStateUpdate } from '../../utils/TylerState';
import { monthlyEquivalent, getFXRate } from '../../utils/forecastEngine';
import CashFlowHistory from './CashFlowHistory';
import EditModal, { Field, FieldGroup } from './EditModal';
import {
  CashFlowItem,
  CashFlowFrequency,
  ForecastAccount,
  Currency,
  CURRENCY_SYMBOLS,
  resolveCategories,
} from '../../types/personalForecast';

let nextId = 1;
function generateId(): string {
  return `cf-${Date.now()}-${nextId++}`;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const FREQUENCY_LABELS: Record<CashFlowFrequency, string> = {
  monthly: 'Monthly',
  biweekly: 'Biweekly',
  'semi-monthly': 'Semi-Monthly',
  weekly: 'Weekly',
  annual: 'Annual',
  quarterly: 'Quarterly',
  'one-time': 'One-Time',
};

function newBlankCashFlow(isInflow: boolean): CashFlowItem {
  return {
    id: generateId(),
    name: '',
    accountId: TylerState.forecast.accounts[0]?.id || '',
    amount: 0,
    frequency: 'monthly',
    startDate: new Date().toISOString().slice(0, 7),
    endDate: '',
    annualGrowthRate: 0,
    category: isInflow ? 'Salary' : 'Housing',
    isInflow,
    sortOrder: TylerState.forecast.cashFlows.length,
    firstPayDate: '',
  };
}

/**
 * Dates by age, using calendar-year age: "age 60" is the year you turn 60. An item runs FROM the
 * January of that year, and an item stops UNTIL that year - so its last month is the December
 * before. "Full time until 60, half time from 60" then lines up with no gap and no overlap.
 */
export const monthFromAge = (age: number, birthYear: number, bound: 'from' | 'until'): string =>
  bound === 'from' ? `${birthYear + age}-01` : `${birthYear + age - 1}-12`;

export const ageFromMonth = (month: string, birthYear: number, bound: 'from' | 'until'): number | null => {
  if (!month || !birthYear) return null;
  const year = Number(month.slice(0, 4));
  const monthNum = Number(month.slice(5, 7));
  // Only show an age back if the date really is a clean "from January" or "until December" one;
  // anything mid-year has no single age that describes it, so the box stays blank.
  if (bound === 'from') return monthNum === 1 ? year - birthYear : null;
  return monthNum === 12 ? year - birthYear + 1 : null;
};

/**
 * Would picking `candidate` as a "% of" source for `targetId` create a loop? True when the
 * candidate already depends on the target, directly or through other items. Chains are fine -
 * part-time pay as a share of salary, then an employer match as a share of both - only circles
 * aren't, and the engine has no way to resolve one.
 */
export function dependsOn(candidate: CashFlowItem, targetId: string, all: CashFlowItem[]): boolean {
  const seen = new Set<string>();
  const walk = (item: CashFlowItem): boolean => {
    if (item.id === targetId) return true;
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return (item.percentOf?.itemIds || []).some(id => {
      const next = all.find(o => o.id === id);
      return next ? walk(next) : false;
    });
  };
  return walk(candidate);
}

/**
 * Where the money comes from and where it goes, so direction reads straight off the row instead of
 * being encoded in isInflow plus a transfer field the engine silently overrides.
 *   income:   Income -> TD          spending: TD -> Spent        transfer: TD -> Mortgage 1
 * A transfer names both ends whatever isInflow says, because toAccountId is what decides.
 */
export const shortName = (accounts: ForecastAccount[], id: string) =>
  (accounts.find(a => a.id === id)?.name || '(unnamed)').replace(/\s*\(.*?\)\s*/, ' ').trim();

export const isMoneyIn = (cf: CashFlowItem) => !cf.toAccountId && cf.isInflow;

export const fromLabel = (cf: CashFlowItem, accounts: ForecastAccount[]) =>
  !cf.toAccountId && cf.isInflow ? 'Income' : shortName(accounts, cf.accountId);

export const toLabel = (cf: CashFlowItem, accounts: ForecastAccount[]) =>
  cf.toAccountId ? shortName(accounts, cf.toAccountId)
    : (cf.isInflow ? shortName(accounts, cf.accountId) : 'Spent');

const CashFlowsManager: React.FC = () => {
  useTylerStateUpdate();
  const cashFlows = TylerState.forecast.cashFlows;
  const accounts = TylerState.forecast.accounts;
  const viewMode = TylerState.forecast.settings.viewMode;
  const birthYear = TylerState.forecast.settings.birthYear;
  const categories = resolveCategories(TylerState.forecast.settings, cashFlows);
  /** Items left behind by a category that was deleted in Settings. */
  const orphaned = cashFlows.filter(cf => !categories.includes(cf.category));
  const baseCurrency = TylerState.forecast.settings.baseCurrency;
  const [editingId, setEditingId] = useState<string | null>(null);

  const updateCF = (id: string, field: keyof CashFlowItem, value: any) => {
    const idx = cashFlows.findIndex(cf => cf.id === id);
    if (idx < 0) return;
    (cashFlows[idx] as any)[field] = value;
    TylerState.forecast.isDirty = true;
    updateReact();
  };

  /** Set a start or end date by age instead of hunting for the calendar month. */
  const ageBox = (cf: CashFlowItem, field: 'startDate' | 'endDate', bound: 'from' | 'until') => {
    if (!birthYear) return null;
    const age = ageFromMonth(cf[field] || '', birthYear, bound);
    return (
      <div style={{ display: 'flex', gap: '3px', alignItems: 'center', marginTop: '3px', fontSize: '0.7rem', color: '#64748b' }}>
        {bound} age
        <input
          type="number"
          value={age ?? ''}
          placeholder="-"
          min="0"
          onChange={e => {
            const next = parseInt(e.target.value, 10);
            updateCF(cf.id, field, Number.isNaN(next) ? '' : monthFromAge(next, birthYear, bound));
          }}
          style={{ width: '38px', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px', textAlign: 'right', fontSize: '0.7rem' }}
        />
      </div>
    );
  };

  const addCashFlow = (isInflow: boolean) => {
    const cf = newBlankCashFlow(isInflow);
    TylerState.forecast.cashFlows.push(cf);
    TylerState.forecast.isDirty = true;
    setEditingId(cf.id);
    updateReact();
  };

  const deleteCashFlow = (id: string) => {
    TylerState.forecast.cashFlows = cashFlows.filter(cf => cf.id !== id);
    TylerState.forecast.isDirty = true;
    updateReact();
  };

  const inflows = cashFlows.filter(cf => cf.isInflow);
  const outflows = cashFlows.filter(cf => !cf.isInflow);
  // Grouped by category, money in before money out, then by name - so a property's rent, costs,
  // interest and principal sit together and read the way its schedule does. Insertion order put
  // "Salary (net) - long term" fourteen rows below "Salary (net)", which is nobody's mental model.
  const ordered = [...cashFlows].sort((a, b) =>
    (a.category || '').localeCompare(b.category || '')
    || Number(isMoneyIn(b)) - Number(isMoneyIn(a))
    || (a.name || '').localeCompare(b.name || ''));

  // Cards count cash only: items on current-bucket accounts, averaged over the year, in the base currency.
  // Employer contributions and transfers into savings or retirement never touch chequing, so they sit apart.
  const accountOf = (cf: CashFlowItem) => accounts.find(a => a.id === cf.accountId);
  const inBaseCurrency = (cf: CashFlowItem) => {
    const acct = accountOf(cf);
    return monthlyEquivalent(cf, cashFlows) * (acct ? getFXRate(acct.currency, baseCurrency, TylerState.forecast.fxRates) : 1);
  };
  const isCash = (cf: CashFlowItem) => accountOf(cf)?.bucket === 'current';
  const totalMonthlyInflows = inflows.filter(isCash).reduce((sum, cf) => sum + inBaseCurrency(cf), 0);
  const totalMonthlyOutflows = outflows.filter(isCash).reduce((sum, cf) => sum + inBaseCurrency(cf), 0);
  const netMonthlyCashFlow = totalMonthlyInflows - totalMonthlyOutflows;
  const monthlyContributions = inflows.filter(cf => !isCash(cf)).reduce((sum, cf) => sum + inBaseCurrency(cf), 0);

  const formatBalance = (amount: number, currency: Currency = baseCurrency) => {
    const sym = CURRENCY_SYMBOLS[currency];
    const abs = Math.abs(amount);
    const formatted = abs.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return amount < 0 ? `-${sym}${formatted}` : `${sym}${formatted}`;
  };


  const sourceNames = (cf: CashFlowItem) =>
    (cf.percentOf?.itemIds || []).map(id => cashFlows.find(o => o.id === id)?.name || '?').join(' + ');

  const amountText = (cf: CashFlowItem) => {
    if (cf.percentOfBalance) return `${cf.percentOfBalance.annualRate}% a year`;
    if (cf.percentOf) return formatBalance(monthlyEquivalent(cf, cashFlows));
    return formatBalance(cf.amount);
  };

  const amountDetail = (cf: CashFlowItem) => {
    if (cf.percentOfBalance) {
      return `${cf.percentOfBalance.annualRate}% a year on ${shortName(accounts, cf.percentOfBalance.accountId)}, falling as it is paid down`;
    }
    if (cf.percentOf) return `${cf.percentOf.percent}% of ${sourceNames(cf)}`;
    return undefined;
  };

  /** Growth, its phases and any seasonality, as a tooltip - the detail is in the modal. */
  const growthSummary = (cf: CashFlowItem) => {
    const parts: string[] = [];
    if (cf.annualGrowthRate) {
      parts.push(`${cf.annualGrowthRate}% a year${cf.growthMonth ? ` each ${MONTH_NAMES[cf.growthMonth - 1]}` : ''}`);
    }
    if (cf.growthPhaseYears !== undefined) {
      parts.push(`then ${cf.longTermGrowthRate ?? cf.annualGrowthRate}% after ${cf.growthPhaseYears} years`);
    }
    if (cf.activeMonths?.length) {
      parts.push(`only in ${cf.activeMonths.map(m => MONTH_NAMES[m - 1]).join(', ')}`);
    }
    return parts.join(', ') || undefined;
  };

  const endCell: React.CSSProperties = {
    maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };

  const editing = cashFlows.find(cf => cf.id === editingId);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '5px 7px', border: '1px solid #cbd5e1', borderRadius: '4px',
  };

  const chosenIds = editing?.percentOf?.itemIds || [];
  /** Picked items, in the order they were added, so the list reads as the user built it. */
  const chosenSources = chosenIds
    .map(id => cashFlows.find(o => o.id === id))
    .filter(Boolean) as CashFlowItem[];
  /** Everything still offerable: not itself, not already picked, and not a circular reference. */
  const availableSources = editing
    ? cashFlows.filter(o => o.id !== editing.id && !chosenIds.includes(o.id)
        && !dependsOn(o, editing.id, cashFlows))
    : [];
  const setSources = (itemIds: string[]) =>
    editing && updateCF(editing.id, 'percentOf', { itemIds, percent: editing.percentOf?.percent ?? 0 });

  const editModal = editing && (
    <EditModal
      title={`${editing.isInflow ? 'Income' : 'Expense'}: ${editing.name || '(unnamed)'}`}
      onClose={() => setEditingId(null)}
      onDelete={() => { deleteCashFlow(editing.id); setEditingId(null); }}
    >
      <Field label="Name" wide>
        <input type="text" value={editing.name} autoFocus style={inputStyle}
          onChange={e => updateCF(editing.id, 'name', e.target.value)} />
      </Field>

      {/* The same From and To the table shows, so the row and the form say the same thing:
          Income -> an account, an account -> Spent, or an account -> another account. */}
      <Field label="From" hint="Where the money comes from. Income means it arrives from outside - pay, rent, a refund.">
        <select
          value={isMoneyIn(editing) ? 'income' : editing.accountId}
          style={inputStyle}
          onChange={e => {
            if (e.target.value === 'income') {
              updateCF(editing.id, 'isInflow', true);
              updateCF(editing.id, 'toAccountId', undefined);
            } else {
              updateCF(editing.id, 'accountId', e.target.value);
              if (isMoneyIn(editing)) updateCF(editing.id, 'isInflow', false);
            }
          }}
        >
          <option value="income">Income (from outside)</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name || '(unnamed)'}</option>)}
        </select>
      </Field>

      <Field label="To" hint="Where it ends up. Spent means gone for good; another account keeps it on your balance sheet - a contribution, or a mortgage payment, which is trimmed to what's still owed.">
        <select
          value={isMoneyIn(editing) ? editing.accountId : (editing.toAccountId || 'spent')}
          style={inputStyle}
          onChange={e => {
            if (isMoneyIn(editing)) {
              updateCF(editing.id, 'accountId', e.target.value);
            } else if (e.target.value === 'spent') {
              updateCF(editing.id, 'toAccountId', undefined);
            } else {
              updateCF(editing.id, 'toAccountId', e.target.value);
            }
          }}
        >
          {/* Money arriving has to land somewhere, so Spent is not on offer for it. */}
          {!isMoneyIn(editing) && <option value="spent">Spent (gone for good)</option>}
          {accounts.filter(a => isMoneyIn(editing) || a.id !== editing.accountId)
            .map(a => <option key={a.id} value={a.id}>{a.name || '(unnamed)'}</option>)}
        </select>
      </Field>

      <Field label="Amount">
        <input type="number" value={editing.amount || ''} placeholder="0" style={inputStyle}
          onChange={e => updateCF(editing.id, 'amount', parseFloat(e.target.value) || 0)} />
      </Field>

      <Field label="Frequency">
        <select value={editing.frequency} style={inputStyle}
          onChange={e => updateCF(editing.id, 'frequency', e.target.value)}>
          {(Object.keys(FREQUENCY_LABELS) as CashFlowFrequency[]).map(f =>
            <option key={f} value={f}>{FREQUENCY_LABELS[f]}</option>)}
        </select>
      </Field>

      {editing.frequency === 'biweekly' && (
        <Field label="First pay date" hint="Used to work out which months get three paycheques.">
          <input type="date" value={editing.firstPayDate} style={inputStyle}
            onChange={e => updateCF(editing.id, 'firstPayDate', e.target.value)} />
        </Field>
      )}

      <Field label="Category" hint="Rename, add or remove these in Settings.">
        <select value={editing.category} style={inputStyle}
          onChange={e => updateCF(editing.id, 'category', e.target.value)}>
          {!categories.includes(editing.category) && (
            <option value={editing.category}>{editing.category || '(none)'} — no longer a category</option>
          )}
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      {viewMode !== 'simple' && <>
        <FieldGroup title="When" />
        <Field label="Starts">
          <input type="month" value={editing.startDate} style={inputStyle}
            onChange={e => updateCF(editing.id, 'startDate', e.target.value)} />
          {ageBox(editing, 'startDate', 'from')}
        </Field>
        <Field label="Ends" hint="Leave blank to run for the whole forecast.">
          <input type="month" value={editing.endDate} style={inputStyle}
            onChange={e => updateCF(editing.id, 'endDate', e.target.value)} />
          {ageBox(editing, 'endDate', 'until')}
        </Field>
        <Field label="Only in these months" wide
          hint="Seasonal income or costs. Hold Ctrl or Cmd to pick several; leave empty for every month.">
          <select multiple value={(editing.activeMonths || []).map(String)}
            style={{ ...inputStyle, height: '62px' }}
            onChange={e => {
              const months = Array.from(e.target.selectedOptions).map(o => Number(o.value));
              updateCF(editing.id, 'activeMonths', months.length ? months : undefined);
            }}>
            {MONTH_NAMES.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
          </select>
        </Field>

        <FieldGroup title="Growth" />
        <Field label="Annual growth %">
          <input type="number" step="0.1" value={editing.annualGrowthRate || ''} placeholder="0"
            style={inputStyle}
            onChange={e => updateCF(editing.id, 'annualGrowthRate', parseFloat(e.target.value) || 0)} />
        </Field>
        <Field label="Applied" hint="A raise effective every August steps up once a year. Spread smooths it across the months.">
          <select value={editing.growthMonth || ''} style={inputStyle}
            onChange={e => updateCF(editing.id, 'growthMonth', e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">spread across the year</option>
            {MONTH_NAMES.map((name, i) => <option key={name} value={i + 1}>each {name}</option>)}
          </select>
        </Field>
        <Field label="Switch rate after (years)" hint="Blank keeps one rate for the whole forecast.">
          <input type="number" min="0" value={editing.growthPhaseYears ?? ''} placeholder="never"
            style={inputStyle}
            onChange={e => {
              const years = parseInt(e.target.value, 10);
              updateCF(editing.id, 'growthPhaseYears', Number.isNaN(years) ? undefined : years);
            }} />
        </Field>
        <Field label="Then grow at %">
          <input type="number" step="0.1" value={editing.longTermGrowthRate ?? ''} placeholder="same"
            style={inputStyle}
            onChange={e => {
              const rate = parseFloat(e.target.value);
              updateCF(editing.id, 'longTermGrowthRate', Number.isNaN(rate) ? undefined : rate);
            }} />
        </Field>

        <FieldGroup title="Or: a rate on a debt or account balance" />
        <Field label="Annual rate %"
          hint="Mortgage interest as a rate on what's still owed. It falls as the balance falls and stops at zero, so it needs no end date.">
          <input type="number" step="0.01" placeholder="e.g. 3.33" style={inputStyle}
            value={editing.percentOfBalance?.annualRate ?? ''}
            onChange={e => {
              const annualRate = parseFloat(e.target.value);
              updateCF(editing.id, 'percentOfBalance', Number.isNaN(annualRate)
                ? undefined : { accountId: editing.percentOfBalance?.accountId || '', annualRate });
            }} />
        </Field>
        <Field label="Of this balance">
          <select value={editing.percentOfBalance?.accountId || ''} style={inputStyle}
            onChange={e => updateCF(editing.id, 'percentOfBalance', e.target.value
              ? { accountId: e.target.value, annualRate: editing.percentOfBalance?.annualRate ?? 0 }
              : undefined)}>
            <option value="">— none —</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name || '(unnamed)'}</option>)}
          </select>
        </Field>

        <FieldGroup title="Or: a share of other items" />
        <Field label="Percent"
          hint="Leave blank to use the Amount above instead.">
          <input type="number" step="0.5" value={editing.percentOf?.percent ?? ''} placeholder="% of"
            style={inputStyle}
            onChange={e => {
              const percent = parseFloat(e.target.value);
              updateCF(editing.id, 'percentOf', Number.isNaN(percent)
                ? undefined : { itemIds: editing.percentOf?.itemIds || [], percent });
            }} />
        </Field>
        <Field label="How it tracks" hint="Prior year is what part-time pay needs: half of a salary that has stopped.">
          <select value={editing.growthPhaseYears === 0 ? 'priorYear' : 'everyMonth'} style={inputStyle}
            onChange={e => updateCF(editing.id, 'growthPhaseYears', e.target.value === 'priorYear' ? 0 : undefined)}>
            <option value="everyMonth">tracked every month</option>
            <option value="priorYear">of the prior year, then grows on its own</option>
          </select>
        </Field>
        {/* Picked items are listed, one per row, each removable. Adding is a plain dropdown,
            grouped by category so a property's items sit together. A ctrl-click multi-select hid
            what was chosen, lost the lot on a stray click, and made order matter. */}
        <Field label="Of these items" wide
          hint={chosenSources.length
            ? `${chosenSources.length} picked. ${editing.percentOf?.percent ?? 0}% of these added together.`
            : 'Nothing picked yet - add the items this is a share of.'}>
          <div>
            {chosenSources.map(src => (
              <div key={src.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '8px', padding: '4px 8px', marginBottom: '3px', borderRadius: '4px',
                background: '#f1f5f9', fontSize: '0.8rem',
              }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {src.name || '(unnamed)'}
                  <span style={{ color: '#94a3b8', marginLeft: '6px' }}>{src.category}</span>
                </span>
                <button
                  onClick={() => setSources((editing.percentOf?.itemIds || []).filter(id => id !== src.id))}
                  title="Remove"
                  style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer',
                    fontSize: '1rem', lineHeight: 1, padding: '0 2px' }}
                >
                  ×
                </button>
              </div>
            ))}
            <select
              value=""
              style={{ ...inputStyle, marginTop: chosenSources.length ? '4px' : 0 }}
              onChange={e => {
                if (!e.target.value) return;
                setSources([...(editing.percentOf?.itemIds || []), e.target.value]);
              }}
            >
              <option value="">+ Add an item…</option>
              {Array.from(new Set(availableSources.map(o => o.category))).sort().map(cat => (
                <optgroup key={cat} label={cat}>
                  {availableSources.filter(o => o.category === cat).map(o => (
                    <option key={o.id} value={o.id}>{o.name || '(unnamed)'}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </Field>
      </>}
    </EditModal>
  );

  const renderRow = (cf: CashFlowItem) => {
    return (
      <tr key={cf.id} className="account-row" onClick={() => setEditingId(cf.id)}>
        <td className="name-cell" title={cf.name}>{cf.name || '(unnamed)'}</td>
        <td className="nowrap" title={fromLabel(cf, accounts)} style={endCell}>{fromLabel(cf, accounts)}</td>
        <td className="nowrap" title={toLabel(cf, accounts)} style={endCell}>{toLabel(cf, accounts)}</td>
        <td className="nowrap" title={amountDetail(cf)}
          style={{ textAlign: 'right', fontWeight: 600, color: isMoneyIn(cf) ? '#059669' : '#dc2626' }}>
          {amountText(cf)}
        </td>
        <td className="nowrap">{FREQUENCY_LABELS[cf.frequency]}</td>
        <td className="nowrap">
          {categories.includes(cf.category)
            ? cf.category
            : <span style={{ color: '#dc2626', fontWeight: 600 }}
                title="This category was deleted in Settings. Pick another, or add it back.">
                ⚠ {cf.category || '(none)'}
              </span>}
        </td>
        {viewMode !== 'simple' && (
          <>
            <td className="nowrap" title={growthSummary(cf)}>{cf.startDate}</td>
            <td className="nowrap" title={growthSummary(cf)}>{cf.endDate || 'Ongoing'}</td>
          </>
        )}
        <td>
          <button
            onClick={e => { e.stopPropagation(); deleteCashFlow(cf.id); }}
            style={{ padding: '2px 8px', background: 'none', color: '#94a3b8', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}
            title="Delete"
          >
            x
          </button>
        </td>
      </tr>
    );
  };

  // One list, with direction read off each row's From and To. Money in sorts above money out so
  // the shape of a month is still obvious at a glance.
  const renderTable = (title: string, list: CashFlowItem[]) => (
    <div className="account-section" style={{ marginBottom: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button className="btn-secondary" onClick={() => addCashFlow(true)}
            style={{ padding: '6px 16px', fontSize: '0.9rem' }}>
            + Money in
          </button>
          <button className="btn-secondary" onClick={() => addCashFlow(false)}
            style={{ padding: '6px 16px', fontSize: '0.9rem' }}>
            + Money out
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <p style={{ color: '#94a3b8', fontStyle: 'italic', padding: '1rem 0' }}>
          Nothing here yet. Add money in or money out to start.
        </p>
      ) : (
        <div className="frozen-table-wrap">
          <table className="accounts-table compact frozen-table sticky-actions">
            <thead>
              <tr>
                <th>Name</th>
                <th>From</th>
                <th>To</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Frequency</th>
                <th>Category</th>
                {viewMode !== 'simple' && (
                  <>
                    <th>Start</th>
                    <th>End</th>
                  </>
                )}
                <th style={{ width: '100px' }}></th>
              </tr>
            </thead>
            <tbody>
              {list.map(renderRow)}
              <tr className="total-row">
                <td colSpan={3}><strong>Monthly net</strong></td>
                {(() => {
                  const net = list.reduce((sum, cf) =>
                    sum + monthlyEquivalent(cf, cashFlows) * (isMoneyIn(cf) ? 1 : -1), 0);
                  return (
                    <td style={{ textAlign: 'right', fontWeight: 700, color: net >= 0 ? '#059669' : '#dc2626' }}>
                      {formatBalance(net)}
                    </td>
                  );
                })()}
                <td colSpan={viewMode !== 'simple' ? 5 : 3} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {editModal}
      {/* Summary cards */}
      <div className="summary-cards" style={{ marginBottom: '2rem' }}>
        <div className="summary-card positive">
          <div className="card-label">Monthly Cash In</div>
          <div className="card-value">{formatBalance(totalMonthlyInflows)}</div>
        </div>
        <div className="summary-card negative">
          <div className="card-label">Monthly Cash Out</div>
          <div className="card-value">{formatBalance(totalMonthlyOutflows)}</div>
        </div>
        <div className={`summary-card ${netMonthlyCashFlow >= 0 ? 'positive' : 'negative'}`}>
          <div className="card-label">Net Cash (current accounts)</div>
          <div className="card-value">{formatBalance(netMonthlyCashFlow)}</div>
        </div>
      </div>
      {monthlyContributions > 0 && (
        <p className="help-text" style={{ marginTop: '-1.25rem', marginBottom: '1.5rem' }}>
          Plus {formatBalance(monthlyContributions)} a month going into savings and retirement accounts (employer contributions and transfers), averaged over the year and not counted above. Seasonal and annual items are averaged too.
        </p>
      )}

      {orphaned.length > 0 && (
        <div style={{ padding: '0.8rem 1rem', background: '#fee2e2', borderRadius: '8px',
          marginBottom: '1rem', color: '#991b1b' }}>
          <strong>{orphaned.length} item{orphaned.length === 1 ? '' : 's'} point at a category that
          no longer exists.</strong> They still count in the forecast, but under a name you removed,
          so they will not group with anything. Give each one a category, or add the old name back in
          Settings: {Array.from(new Set(orphaned.map(cf => cf.category || '(none)'))).join(', ')}.
        </div>
      )}

      {accounts.length === 0 && (
        <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '8px', marginBottom: '1rem', color: '#92400e' }}>
          Create accounts first in "What I Own & Owe" — cash flows link to accounts.
        </div>
      )}

      {renderTable('Cash Flows', ordered)}
      <CashFlowHistory />
    </div>
  );
};

export default CashFlowsManager;
